/**
 * The URL a LinkedIn post column cell should open.
 *
 * A value stored in per-article mode carries a top-level `share_url`. In the default
 * aggregated mode the API writes `value = { ...posts, answer }` and puts the URL on the
 * column value row's own `sourceUrl` (list-column-linkedin.service.ts / tracker.service.ts),
 * so the value itself has no URL at all (PREC-4980). Falling back to
 * `columnValueSourceUrl` is therefore the production case, not a last resort: without it
 * the icon opens a blank tab for every long-form cell.
 *
 * Every candidate is trimmed and a blank or non-string one is skipped, so a malformed
 * stored value (a string, a number, an array) answers `null` instead of throwing.
 */
export function linkedinPostColumnUrl(
	value: unknown,
	columnValueSourceUrl?: string | null
): string | null {
	const firstUrl = (candidate: unknown): string | null => {
		if (typeof candidate !== "string") {
			return null;
		}

		const trimmed = candidate.trim();

		return trimmed.length > 0 ? trimmed : null;
	};

	const record =
		typeof value === "object" && value !== null && !Array.isArray(value)
			? (value as Record<string, unknown>)
			: {};

	return firstUrl(record.share_url) ?? firstUrl(record.sourceUrl) ?? firstUrl(columnValueSourceUrl);
}
